/**
 * The numbers the landing and pricing pages claim about the content.
 *
 * `content/` must never be imported from `src/` — it carries the answer keys —
 * so the counts cannot be derived at runtime and have to be written down. They
 * are written down *here*, in one place, and `tests/landing.test.ts` counts the
 * real content and fails when a claim stops being true.
 *
 * What a candidate has a reason to care about: how many whole exams there are
 * to sit, how many are open before anyone asks for anything, and — on the
 * pricing page, where the question is whether the library is worth paying for —
 * how much is actually in it.
 */
export const LANDING_NUMBERS = {
  /** Sets with every module present: the full 115-minute exam. */
  fullSets: 16,
  /** Sets open before paying: one telc paper and one state paper per level. */
  freeSets: 3,
  /** Auto-marked items across the full sets — listening + reading + elements. */
  questions: 944,
  /** Recordings in the listening modules of the full sets. */
  recordings: 208,
  /** Items in the Elementy języka modules of the full sets. */
  grammarItems: 288,
  /** Writing briefs across the full sets (each set offers a choice of two). */
  writingBriefs: 32,
  /** Oral-exam topic sheets, each practisable on its own. */
  speakingTopics: 24,
  /** Real sittings of the state exam in the library, at B1 and B2. */
  certyfikatPapers: 23,
  /**
   * The same papers, split by the level they certify.
   *
   * A candidate sits one level, so half the library is somebody else's exam —
   * and "23 papers, at B1 and B2" left every reader of the cold read counting
   * on their fingers. The split is the first thing they can act on, so it is
   * written down here rather than left to be inferred.
   */
  certyfikatB1: 14,
  certyfikatB2: 9,
  /**
   * Practice sets of our own in the state exam's format, beside the real
   * papers and never counted among them — the papers are the Commission's, and
   * a page that folded the two together would be selling ours as theirs.
   */
  certyfikatOwnB1: 6,
  certyfikatOwnB2: 5,
  /**
   * All of ours, both levels. \`StateTasksPage\` mentions them only while this
   * is above zero, so the copy never claims a set that is not in the library.
   */
  certyfikatOwn: 11,
} as const;

/**
 * The day the library last grew, `YYYY-MM-DD`.
 *
 * The public pages promise at least one new set a month. No test can hold a
 * promise about the future — what it can do is stand a date next to it, the
 * way `SITTINGS_CHECKED` stands next to the Commission's calendar: a reader
 * who can see when the library last grew can check the promise themselves,
 * and a date that has stopped moving says so louder than any copy.
 *
 * Move it in the same commit that adds sets to `content/certyfikat-own/`.
 */
export const LIBRARY_GREW = "2026-10-07";

/**
 * The newest paper on the Commission's own past-papers page, and the day a
 * person last read that page.
 *
 * This is the whole argument for writing sets of our own, so it is the one
 * claim on these pages about somebody else's conduct — and it is checkable in
 * one click, which is why the date it was checked is recorded beside it. On
 * 2026-09-23 the page listed nothing after the 22-23 June 2024 sitting: no
 * October 2024, no 2025, no 2026, while `/dla-zdajacych/przykladowe-testy/`
 * still promises "Testy z kolejnych lat będą systematycznie publikowane".
 *
 * **The copy never writes the gap in words.** "Two years" is true today and
 * quietly false next spring; the pages compute it from this date, so it ages
 * by itself. Re-read the page when a new sitting is added, and if the
 * Commission has published again, this moves and `certyfikatPapers` with it.
 *
 * Not every published paper is in the library: on the same reading the page
 * listed 15 adult B1 papers to our 14 (the 25-26 June 2022 sitting is not
 * here) and 9 B2 to our 9. So copy says how many we hold, never "all of them".
 */
export const LAST_PUBLISHED_PAPER = "2024-06-22";
export const PAPERS_CHECKED = "2026-09-23";
export const PAPERS_SOURCE =
  "https://certyfikatpolski.pl/o-egzaminie/testy-egzaminacyjne-z-poprzednich-lat/";

/**
 * The day somebody last read what the law asks for citizenship — the state
 * certificate at B1 — and the bills that would change it. Printed beside the
 * claim on `/obywatelstwo` and `/certyfikat/b2`; move it when the page is
 * re-read against the law, not otherwise.
 */
export const LAW_CHECKED = "2026-09-29";

/**
 * The one day the facts on the informational pages — thresholds, times, what
 * each exam is accepted for — were last read against the examining bodies'
 * own pages. Printed on every such page as "Facts checked"; there is one date
 * for the whole site so no page can quietly be older than the rest. Move it
 * when the pages are re-read, not when a line of copy changes.
 */
export const FACTS_CHECKED = "2026-09-29";
